import { useEffect, useState } from 'react';
import type { CurrentUserQuery } from '../../graphql';
import { Currency, useCurrencyApiMutation } from '../../graphql';
import { useSetCurrencyRates } from '../../state/zustand';
import { useNotify } from '../notifications/ToastNotifications';

export function useCurrencyApi(
  currentUser?: CurrentUserQuery['currentUser'],
): boolean {
  const [dataReady, setDataReady] = useState(false);
  const skip = !currentUser;
  const baseCurrency =
    currentUser?.organization?.defaultCurrency ??
    currentUser?.rootOrganization?.defaultCurrency ??
    Currency.EUR;

  const setCurrencyRates = useSetCurrencyRates();
  const [currencyApiMutation] = useCurrencyApiMutation();

  const notify = useNotify({
    toastId: 'currencyApi',
  });

  useEffect(() => {
    if (skip) return;

    let isMounted = true;

    const fetchCurrencyRates = async () => {
      try {
        const result = await currencyApiMutation({
          variables: { baseCurrency },
        });

        if (!isMounted) return;

        const currencyData = result.data?.currencyApi;

        if (currencyData?.message) {
          notify(`Failed to fetch currency rates: ${currencyData.message}`, {
            autoClose: false,
            type: 'error',
          });
          console.error(
            'Failed to fetch currency rates:',
            currencyData.message,
          );
        } else if (currencyData?.rates && currencyData.baseCurrency) {
          setCurrencyRates(currencyData.baseCurrency, currencyData.rates);
        }
      } catch (error) {
        if (!isMounted) return;

        const errorMessage =
          error instanceof Error ? error.message : String(error);
        notify(`Failed to fetch currency rates: ${errorMessage}`, {
          autoClose: false,
          type: 'error',
        });
        console.error('Failed to fetch currency rates:', error);
      } finally {
        if (isMounted) {
          setDataReady(true);
        }
      }
    };

    fetchCurrencyRates();

    return () => {
      isMounted = false;
    };
  }, [currencyApiMutation, skip, baseCurrency, notify, setCurrencyRates]);

  return dataReady;
}
